import React, { useEffect, useRef } from "react";
import studyDenBear from "../assets/images/branding/Studyden_Bear_Only.svg";

const answers = (count) => `${count} ${count === 1 ? "answer" : "answers"}`;

// Derives only what the marking workflow actually reports. `processedCount` is
// the number of unique answers the marking API has returned in this run, so it
// cannot double-count across retry passes; it is capped at the run total.
export const describeMarkingProgress = (progress = {}, processedCount = 0) => {
  const total = Math.max(0, Number(progress.total) || 0);
  const processed = Math.min(total, Math.max(0, Number(processedCount) || 0));
  const failed = Math.max(0, Number(progress.failed) || 0);
  const verificationFailed = Math.max(0, Number(progress.verificationFailed) || 0);
  const awaitingFirstResult = total === 0 || (processed === 0 && failed === 0 && !progress.verificationPhase);
  const percentage = total > 0 ? Math.floor((processed / total) * 100) : 0;
  const countLabel = total > 0
    ? `${processed} of ${answers(total)} processed`
    : "Preparing answers for marking";
  return {
    total,
    processed,
    percentage,
    awaitingFirstResult,
    countLabel,
    valueText: awaitingFirstResult
      ? "Waiting for the first results"
      : `${countLabel} (${percentage}%)`,
    batchLabel: progress.currentBatch > 0 && progress.totalBatches > 0
      ? `Batch ${progress.currentBatch} of ${progress.totalBatches}`
      : null,
    retryLabel: progress.retryAttempts > 0
      ? `${progress.retryAttempts} ${progress.retryAttempts === 1 ? "retry attempt" : "retry attempts"} so far`
      : null,
    verificationPhase: progress.verificationPhase === true,
    pendingRetryLabel: !progress.verificationPhase && failed > 0
      ? `${answers(failed)} not processed yet. These are retried automatically before marking finishes.`
      : null,
    stillFailingLabel: progress.verificationPhase && verificationFailed > 0
      ? `${answers(verificationFailed)} still could not be processed after retrying.`
      : null,
  };
};

const AiMarkingProgressPanel = ({ progress, processedCount }) => {
  const headingRef = useRef(null);
  const view = describeMarkingProgress(progress, processedCount);

  // The start button is disabled once marking begins, so move focus here
  // rather than leaving keyboard and screen reader users on a dead control.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <section
      className="vqr-card vqr-progress-card vqr-marking-panel"
      aria-labelledby="vqr-marking-panel-title"
      aria-describedby="vqr-marking-panel-note"
      aria-busy="true"
    >
      <div className="vqr-marking-emblem" aria-hidden="true">
        <span className="vqr-marking-ring vqr-marking-ring-outer" />
        <span className="vqr-marking-ring vqr-marking-ring-inner" />
        <img src={studyDenBear} alt="" className="vqr-marking-bear" />
      </div>

      <p className="vqr-marking-eyebrow">StudyDen AI marking</p>
      <h2 id="vqr-marking-panel-title" ref={headingRef} tabIndex={-1}>
        Marking your students’ answers…
      </h2>

      <div className="vqr-marking-meter">
        <div className="vqr-marking-count">
          <strong>{view.awaitingFirstResult ? view.valueText : view.countLabel}</strong>
          {!view.awaitingFirstResult && <span>{view.percentage}%</span>}
        </div>
        <div
          className={`vqr-marking-track${view.awaitingFirstResult ? " is-indeterminate" : ""}`}
          role="progressbar"
          aria-label="AI marking progress"
          aria-valuemin={0}
          aria-valuemax={view.awaitingFirstResult ? undefined : view.total}
          aria-valuenow={view.awaitingFirstResult ? undefined : view.processed}
          aria-valuetext={view.valueText}
        >
          <span
            className="vqr-marking-fill"
            style={view.awaitingFirstResult ? undefined : { transform: `scaleX(${view.total ? view.processed / view.total : 0})` }}
          />
        </div>
      </div>

      {(view.batchLabel || view.retryLabel || view.verificationPhase) && (
        <ul className="vqr-marking-details" aria-label="Marking details">
          {view.verificationPhase && <li>Re-checking for unprocessed answers</li>}
          {view.batchLabel && <li>{view.batchLabel}</li>}
          {view.retryLabel && <li>{view.retryLabel}</li>}
        </ul>
      )}

      {(view.pendingRetryLabel || view.stillFailingLabel) && (
        <p className="vqr-marking-attention">{view.pendingRetryLabel || view.stillFailingLabel}</p>
      )}

      <p id="vqr-marking-panel-note" className="vqr-marking-note">
        Processed answers include any that could not be marked. When marking finishes,
        the summary shows how many were marked and which need attention.
      </p>

      <p className="vqr-sr-only" role="status" aria-live="polite">
        {[view.valueText, view.pendingRetryLabel, view.stillFailingLabel].filter(Boolean).join(". ")}
      </p>
    </section>
  );
};

export default AiMarkingProgressPanel;
